import { useRiftArtworkPath } from '../utils/riftArtwork';
import React from 'react';
import type { RiftProgressEntry, UserLanguage } from '../api';
import { GameDialog } from './GameDialog';
import { RiftMarker } from './RiftMarker';
import '../styles/KingsportRifts.css';
import { riftIdentityStyle, RiftPreviewCallback, useRiftPreviewEvents } from './riftPresentation';

type RiftViewProps = {
  entries?: RiftProgressEntry[];
  language: UserLanguage;
  onLocation: (name: string) => void;
  onPreview?: RiftPreviewCallback;
};

function RiftRows({ entries = [], language, onLocation, onOpen, onPreview }: RiftViewProps & { onOpen?: () => void }) {
  const previewEvents = useRiftPreviewEvents(onPreview);
  const artwork = useRiftArtworkPath('back');
  const russian = language === 'Ru';
  return <>{[1, 2, 3].map(id => {
    const entry = entries.find(value => value.id === id);
    const count = (entry?.tracks ?? []).reduce((total, track) => total + (track.locations?.length ?? 0), 0);
    const name = `${russian ? 'Разлом' : 'Rift'} ${id}`;
    const status = entry?.isOpen ? (russian ? 'Открыт' : 'Open') : (russian ? 'Закрыт' : 'Closed');
    const icon = entry?.isOpen && entry?.dimensionalSign && entry?.color ? <RiftMarker rift={{ ...entry, turnsActive: 0 }} size={54} identityColor />
      : <img className="rift-identity-image" src={artwork} width={54} height={54} alt="" draggable={false} />;
    return <section className="kingsport-rift-row" key={id} data-rift-id={id} style={riftIdentityStyle(id)} data-open={!!entry?.isOpen} aria-label={`${name}: ${status}, ${count}/4`}>
      <div className="kingsport-rift-identity">
        <button type="button" className="kingsport-rift-button" aria-label={`${name}: ${status}, ${count}/4`}
           {...previewEvents(`rift-${id}`, { riftId: id })}
          aria-haspopup={onOpen ? "dialog" : undefined} onClick={event => { event.currentTarget.focus({ preventScroll: true }); onOpen?.(); }}>{icon}</button>
        <span>{name}</span><span>{status} · {count}/4</span>
      </div>
      <div className="kingsport-rift-tracks">
        {[0, 1].map(trackIndex => {
          const track = entry?.tracks?.[trackIndex];
          return <div className="kingsport-rift-track" key={trackIndex} role="group"
            aria-label={`${name}, ${russian ? 'трек' : 'track'} ${trackIndex + 1}`}>
            <div className="kingsport-rift-movements">
              {(["White", "Black"] as const).map(color => <div key={color}
                className="kingsport-rift-movement" data-color={color}>
                {(track?.monsterMovements ?? []).filter(movement => movement.color === color).map((movement, index) =>
                  <img key={index} src={`/signs/${encodeURIComponent(movement.dimensionSign)}.svg`}
                    alt={`${movement.color} ${movement.dimensionSign}`} draggable={false} />)}
              </div>)}
            </div>
            <div className="kingsport-rift-spaces">
              {[0, 1].map(space => {
                const location = track?.locations?.[space];
                return location ? <button key={space} type="button" className="kingsport-rift-space" title={location}
                  aria-label={`${russian ? 'Показать' : 'Highlight'} ${location}, ${name}, ${russian ? 'трек' : 'track'} ${trackIndex + 1}, ${space + 1}`}
                  onClick={() => onLocation(location)}
                  {...previewEvents(`space-${id}-${trackIndex}-${space}`, { location })}>
                  <img src={`/rifts/${encodeURIComponent(location)}.png`} alt={location} draggable={false} />
                </button> : <span key={space} className="kingsport-rift-space kingsport-rift-empty"
                  aria-label={russian ? 'Пустое деление' : 'Empty progress space'} />;
              })}
            </div>
          </div>;
        })}
      </div>
    </section>;
  })}</>;
}

export function KingsportRifts(props: RiftViewProps & { onOpen: () => void; style?: React.CSSProperties }) {
  return <section className="kingsport-rifts" style={props.style} aria-label={props.language === 'Ru' ? 'Разломы Кингспорта' : 'Kingsport rifts'}>
    <h3>{props.language === 'Ru' ? 'Разломы Кингспорта' : 'Kingsport rifts'}</h3>
    <RiftRows {...props} />
  </section>;
}

export function KingsportRiftsDialog(props: RiftViewProps & { onClose: () => void }) {
  const russian = props.language === 'Ru';
  return <GameDialog title={russian ? 'Разломы Кингспорта' : 'Kingsport rifts'} variant="location" onClose={props.onClose}>
    <div className="kingsport-rifts-info">
      <RiftRows entries={props.entries} language={props.language} onLocation={props.onLocation} onPreview={props.onPreview} />
      <section className="kingsport-rifts-rules">
        <h3>{russian ? 'Как работают разломы' : 'How rifts work'}</h3>
        {russian ? <>
          <p>В фазе Мифа полный набор символов и цветов движения монстров должен точно совпасть с набором трека. Порядок символов не важен; лишние или недостающие символы не допускаются. При совпадении на трек добавляется один жетон локации.</p>
          <p>Чтобы убрать жетон, сыщик должен провести контакт в указанной локации. Если подходят несколько жетонов, игра предложит выбрать один.</p>
          <p>Когда заполнены все четыре деления, разлом открывается. Чтобы закрыть его, нужно убрать все четыре жетона прогресса.</p>
          <p>Если оба деления трека заполнены, дальнейшие совпадения игнорируются. Пока разлом открыт, новые жетоны прогресса на него не добавляются, даже если часть делений уже очищена.</p>
        </> : <>
          <p>During Mythos, the complete set of monster movement symbols and colors must exactly match a track. Order does not matter; extra or missing symbols prevent a match. A matching track gains one location progress token.</p>
          <p>To remove a token, an investigator must have an encounter at its corresponding location. If several tokens match, the game prompts the investigator to choose one.</p>
          <p>When all four spaces are filled, the rift opens. To close it, remove all four progress tokens.</p>
          <p>A full two-space track ignores further matching progress. An open rift ignores all new progress, even after some of its spaces have been cleared.</p>
        </>}
      </section>
    </div>
  </GameDialog>;
}
